import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import * as Highcharts from "highcharts";
import OfflineExporting from "highcharts/modules/offline-exporting";
import Exporting from "highcharts/modules/exporting";
import Boost from "highcharts/modules/boost";
import noData from "highcharts/modules/no-data-to-display";
import Data from "highcharts/modules/data";
import More from "highcharts/highcharts-more";
import threeD from "highcharts/highcharts-3d";
import lollipop from "highcharts/modules/lollipop";
import dumbbell from "highcharts/modules/dumbbell";
import Histogram from "highcharts/modules/histogram-bellcurve";
import { ChartDataProcess } from "../../models/chart-data-processor/ChartDataProcess";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DatePipe } from "@angular/common";
import { AiroService } from "../../services/airo.service";

Boost(Highcharts);
noData(Highcharts);
More(Highcharts);
noData(Highcharts);
threeD(Highcharts);
Exporting(Highcharts);
OfflineExporting(Highcharts);
dumbbell(Highcharts);
lollipop(Highcharts);
Data(Highcharts);
Histogram(Highcharts);
@Component({
  selector: "app-qna-graph",
  templateUrl: "./qna-graph.component.html",
  styleUrls: ["./qna-graph.component.scss"],
  providers: [DatePipe],
})
export class QnaGraphComponent implements OnInit, OnChanges {
  @Input() convo: any = {};
  @Input() chatIndex: any = {};
  @Input() onlyGraph: boolean = false;
  @Output() adddedToDashboard = new EventEmitter();
  chart: Highcharts.Chart | null = null;
  disableGroupBy = false;

  constructor(
    private readonly airoDataService: AiroService,
    private readonly datePipe: DatePipe
  ) {}

  async exportChart() {
    if (!this.chart || !this.convo.queryResults.length) {
      console.error("No chart or data available to export");
      return;
    }

    try {
      const pdf = new jsPDF("p", "mm", "a4");

      const timestamp =
        this.datePipe.transform(
          new Date(),
          "MMM dd, yyyy HH:mm:ss 'UTC'",
          "UTC"
        ) || "";

      // Footer helper
      const addFooter = (doc: any, timestamp: string) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;

        const footerMargin = 10;
        const message = `This PDF is digitally generated and confidential. Generated on ${timestamp}`;
        const pageText = `Page #${pageNumber}`;

        doc.setFontSize(6);
        doc.setTextColor(100);
        doc.setFont("helvetica", "normal");

        // Left: empty (do nothing)

        // Center: message + timestamp
        doc.text(message, pageWidth / 2, pageHeight - footerMargin, {
          align: "center",
        });

        // Right: page number
        doc.text(pageText, pageWidth - 10, pageHeight - footerMargin, {
          align: "right",
        });
      };

      const originalAddPage = pdf.addPage;

      pdf.addPage = function () {
        originalAddPage.call(this);
        addFooter(this, timestamp); // draw footer immediately on new page
        return this;
      };

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;
      const margin = 20;

      const logo = new Image();
      logo.src = "assets/images/pdf-logo.png"; // ensure this is base64 or served correctly
      pdf.addImage(logo, "PNG", pageWidth - margin - 30, 5, 30, 20);
      const airoLogo = new Image();
      airoLogo.src = "assets/images/airo-black.png";
      pdf.addImage(airoLogo, "PNG", margin, 12, 30, 8);

      // Add title
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 102, 204);
      const title = "Airo Data Report";
      const titleLines = pdf.splitTextToSize(title, pageWidth - 40);
      pdf.text(titleLines, pageWidth / 2, yPosition, { align: "center" });
      yPosition += titleLines.length * 7 + 10;
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      const query = "Query: " + this.convo.question;
      const queryLines = pdf.splitTextToSize(query, pageWidth - 40);
      pdf.text(queryLines, 20, yPosition);
      yPosition += titleLines.length * 7 + 10;

      // Export chart as image
      const chartImageData = this.chart.getSVG();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      // Convert SVG to canvas
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          resolve();
        };
        img.onerror = reject;
        img.src =
          "data:image/svg+xml;base64," +
          btoa(unescape(encodeURIComponent(chartImageData)));
      });

      // Add chart image to PDF
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Check if we need a new page for the chart
      if (yPosition + imgHeight > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.addImage(imgData, "PNG", 20, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 10;

      // Prepare table data
      let tableColumns =
        this.convo.tableColumns ||
        Object.keys(this.convo.queryResults[0] || {});
      const tableRows = this.convo.queryResults.map((row: any) => {
        return tableColumns.map((col: string) => {
          const value = row[col];
          // Format numbers with proper decimal places
          if (typeof value === "number") {
            return value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }
          return value != null ? String(value) : "";
        });
      });

      // Check if we need a new page for the table
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      // Add table title
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("Data Table", 20, yPosition);
      yPosition += 7;

      tableColumns = tableColumns.map((col: any) => {
        if (this.convo.columnsMetadata[col]?.unit) {
          return `${col} (${this.convo.columnsMetadata[col]?.unit})`;
        }
        return col;
      });
      // Add table to PDF
      autoTable(pdf, {
        head: [tableColumns],
        body: tableRows,
        startY: yPosition,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { left: 20, right: 20 },
        didDrawPage: function () {
          addFooter(pdf, timestamp);
        },
      });
      // Generate filename from query or use default
      const filename =
        this.sanitizeFilename(this.getPDFtitle()) || "chart-export";
      const fileTimestamp = this.datePipe.transform(
        new Date(),
        "yyyyMMdd_HHmm"
      );
      pdf.save(`${filename}_${fileTimestamp}.pdf`);

      console.log("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting chart:", error);
    }
  }

  getPDFtitle(): string {
    const selectedGraph = this.convo.selectedGraph;
    const baseTitle =
      this.convo.chart_options?.[selectedGraph]?.title?.text ||
      this.convo.question;
    return `${baseTitle}`;
  }

  private sanitizeFilename(filename: string): string {
    if (!filename) return "chart-export";
    // Remove invalid characters and limit length
    return filename
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase()
      .substring(0, 50);
  }

  ngOnInit(): void {
    if (this.convo.queryResults.length) {
      this.convo.plot_generation_status = false;
      if (this.convo.available_charts.length == 0) {
        this.airoDataService
          .getPlots(this.convo.projectId, this.chatIndex)
          .subscribe((plot_response: any) => {
            this.convo.graphTypes = plot_response.data.available_charts;
            this.convo.imageUrls = plot_response.data.chart_image_urls;
            if (this.convo.selected_graph) {
              this.convo.selectedGraph = this.convo.selected_graph;
            } else {
              this.convo.selectedGraph = this.convo.graphTypes[0];
            }
            this.convo.resChartOptions = plot_response.data.chart_options;
            this.convo.graphs = plot_response.data.chart_options;
            this.convo.plot_generation_status = true;
            this.addChartOptionsData();
          });
      } else {
        this.convo.graphTypes = this.convo.available_charts;
        this.convo.imageUrls = this.convo.chart_image_urls;
        if (this.convo.selected_graph) {
          this.convo.selectedGraph = this.convo.selected_graph;
        } else {
          this.convo.selectedGraph = this.convo.graphTypes[0];
        }
        this.convo.resChartOptions = this.convo.chart_options;
        this.convo.graphs = this.convo.chart_options;
        this.convo.plot_generation_status = true;
        this.addChartOptionsData();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.convo.graphs = this.convo.resChartOptions;
    this.addChartOptionsData();
  }

  availableGraph(graph: any): boolean {
    return [
      "Bar",
      "Line",
      "Plot",
      "Scatter",
      "Pie",
      "Stackplot",
      "Hist",
      "Area",
      "Stairs",
      "Eventplot",
      "Stem",
      "Ecdf",
      "Fill_between",
      "StackBar",
    ].includes(this.convo.selectedGraph);
  }

  addGraphToDashboard(event: any) {
    this.adddedToDashboard.emit(this.convo);
  }

  getMonthNumber(monthName: string): number | string | null {
    if (typeof monthName !== "string") return null;
    const isoDateMatch = monthName
      .trim()
      .match(/^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$/);
    if (isoDateMatch) {
      // If it's a valid ISO date format, return true
      return monthName;
    }
    // Support "January YYYY" -> "YYYY-1"
    const monthYearMatch = monthName.trim().match(/^([a-zA-Z]+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const months = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
      ];
      const monthIdx = months.indexOf(monthYearMatch[1].toLowerCase());
      if (monthIdx !== -1) {
        // Return as YYYY-M (e.g., 2023-1 for January 2023)
        return `${monthYearMatch[2]}-${(monthIdx + 1).toString().padStart(2, "0")}`;
      }
    }

    // YYYY-MM-DD
    // YYYY-MM

    // Old functionality: just month name
    const months = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];
    const idx = months.indexOf(monthName.trim().toLowerCase());
    return idx !== -1 ? idx + 1 : null;
  }

  isMonthString(value: string): boolean {
    if (typeof value !== "string") return false;
    // Check if the value is in the format "YYYY-MM-DD" or "YYYY-MM"
    const isoDateMatch = value.trim().match(/^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$/);
    if (isoDateMatch) {
      // If it's a valid ISO date format, return true
      return true;
    }
    // Support "January YYYY"
    const monthYearMatch = value.trim().match(/^([a-zA-Z]+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const months = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
      ];
      return months.includes(monthYearMatch[1].toLowerCase());
    }
    // Old functionality: just month name
    const months = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];
    return months.includes(value.trim().toLowerCase());
  }
  getValueWithType(value: any) {
    if (!isNaN(parseFloat(value))) {
      return parseFloat(value);
    } else if (!isNaN(new Date(value).getTime())) {
      return new Date(value); // Convert to ISO string for Highcharts
    } else {
      return value;
    }
  }

  parseDate(dateStr: string, format: string | undefined): number | null {
    if (!dateStr || !format) return null;

    // Supported tokens and their regex patterns
    const tokenPatterns: any = {
      YYYY: "(\\d{4})",
      MM: "(\\d{1,2})",
      DD: "(\\d{1,2})",
      HH: "(\\d{1,2})",
      mm: "(\\d{1,2})",
      SS: "(\\d{1,2})",
    };

    // Build regex pattern from format
    let regexPattern = format;
    Object.keys(tokenPatterns).forEach((token) => {
      regexPattern = regexPattern.replace(token, tokenPatterns[token]);
    });

    const regex = new RegExp("^" + regexPattern + "$");
    const match = dateStr.match(regex);
    if (!match) return null;

    // Extract values based on format order
    const formatOrder: any = [];
    let idx = 1;
    let tempFormat = format;
    Object.keys(tokenPatterns).forEach((token) => {
      const pos = tempFormat.indexOf(token);
      if (pos !== -1) {
        formatOrder.push({ token, idx, pos });
        tempFormat = tempFormat.replace(token, " ".repeat(token.length));
        idx++;
      }
    });
    // Sort by position in format string
    formatOrder.sort((a: any, b: any) => a.pos - b.pos);

    // Default values
    let year = 1970,
      month = 1,
      day = 1,
      hour = 0,
      minute = 0,
      second = 0;
    formatOrder.forEach((item: any, i: any) => {
      const value = parseInt(match[i + 1], 10);
      switch (item.token) {
        case "YYYY":
          year = value;
          break;
        case "MM":
          month = value;
          break;
        case "DD":
          day = value;
          break;
        case "HH":
          hour = value;
          break;
        case "mm":
          minute = value;
          break;
        case "SS":
          second = value;
          break;
      }
    });

    // JS Date: month is 0-based
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }

  formateValueWithType(
    value: any,
    valueMetadata: {
      datatype: string;
      format?: string;
      unit?: string;
    }
  ) {
    if (
      valueMetadata.datatype === "int" ||
      valueMetadata.datatype === "float"
    ) {
      return parseFloat(parseFloat(value).toFixed(2));
    } else if (
      valueMetadata.datatype === "datetime" ||
      valueMetadata.datatype === "date" ||
      valueMetadata?.format
    ) {
      return this.parseDate(value, valueMetadata.format);
    } else {
      return value;
    }
  }

  updateHighChart(chartOptions: any) {
    if (!this.availableGraph(this.convo.selectedGraph)) {
      return;
    }
    // Added setTimeout to allow below instruction to run after render
    // to detect div
    setTimeout(() => {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.chart = Highcharts.chart(
        this.chatIndex.toString() + "_hichart",
        chartOptions
      );
    }, 100);
  }

  graphSelectionChange(event: any) {
    this.updateHighChart(this.convo.graphs[event.value]);
  }

  processLineChartGroupBy(chartOptions: any) {
    // Only process for Line charts with group_by column
    if (!chartOptions.groupByCols || chartOptions.groupByCols.length === 0) {
      return chartOptions;
    }

    const groupByCol = chartOptions.groupByCols[0];
    const xAxisCol = chartOptions.xAxisCols[0];
    const yAxisCol = chartOptions.yAxisCols[0];

    // Group data by the specified column
    const groupedData: { [key: string]: any[] } = {};
    let isDateTime = false;
    if (
      this.convo.columnsMetadata[xAxisCol].datatype === "datetime" ||
      this.convo.columnsMetadata[xAxisCol].datatype === "date" ||
      this.convo.columnsMetadata[xAxisCol]?.format
    ) {
      isDateTime = true; // Check if x-axis values are datetime
    }
    this.convo.queryResults.forEach((values: any) => {
      const groupKey = values[groupByCol];
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      let xValue = this.formateValueWithType(
        values[xAxisCol],
        this.convo.columnsMetadata[xAxisCol]
      );
      groupedData[groupKey].push({
        x: xValue,
        y: this.formateValueWithType(
          values[yAxisCol],
          this.convo.columnsMetadata[yAxisCol]
        ),
      });
    });

    // setting XAxis
    chartOptions.xAxis = {
      ...chartOptions.xAxis,
      // type: 'datetime',
      title: {
        text: chartOptions.xAxisCols[0],
      },
    };

    // Configure y-axis
    chartOptions.yAxis = {
      ...chartOptions.yAxis,
      title: {
        text: chartOptions.yAxisCols[0],
      },
    };

    if (isDateTime) {
      // If x-axis values are datetime, set xAxis type to 'datetime'
      chartOptions.xAxis.type = "datetime"; // Ensure x-axis is treated as datetime
      // Set xAxis label format based on datatype
      const xAxisMeta = this.convo.columnsMetadata[xAxisCol];
      if (xAxisMeta?.datatype === "datetime") {
        chartOptions.xAxis.labels = {
          format: "{value:%Y-%m-%d}",
        };
      } else if (xAxisMeta?.datatype === "year") {
        chartOptions.xAxis.labels = {
          formatter: function () {
            // Show only the year part
            const date = new Date(this.value);
            return date.getFullYear();
          },
        };
      } else if (xAxisMeta?.datatype === "month") {
        chartOptions.xAxis.labels = {
          formatter: function () {
            // Show as "Jan,2025"
            const date = new Date(this.value);
            const month = date.toLocaleString("default", { month: "short" });
            const year = date.getFullYear();
            return `${month},${year}`;
          },
        };
      } else {
        chartOptions.xAxis.labels = {
          format: "{value}",
        };
      }
    }
    // Convert grouped data to series
    const series = Object.entries(groupedData).map(([name, data]) => {
      // Sort data points by x-value
      const sortedData = data.sort((a, b) => a.x - b.x);
      return {
        name: name,
        data: sortedData.map((point) => [point.x, point.y]),
      };
    });

    // Update chart options
    chartOptions.series = series;

    if (isDateTime) {
      chartOptions.tooltip = {
        shared: true,
        crosshairs: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const points = this.points || [];
          let tooltipString = `<b>Date: ${this.x}</b><br/>`;
          if (!isNaN(new Date(this.x as any).getTime())) {
            const formattedDate = Highcharts.dateFormat(
              "%Y-%m-%d",
              this.x as any
            );

            tooltipString = `<b>Date: ${formattedDate}</b><br/>`;
          }
          points.forEach((point) => {
            if (typeof point.y === "number") {
              tooltipString += `${point.series.name}: <b>${point.y.toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}</b><br/>`;
            } else {
              tooltipString += `${point.series.name}: ${point.y}<br/>`;
            }
          });
          return tooltipString;
        },
      };
    } else {
      // Enhanced tooltip for grouped line charts
      chartOptions.tooltip = {
        shared: true,
        crosshairs: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const points = this.points || [];

          let tooltipString = `<b>X: ${this.x}</b><br/>`;
          points.forEach((point: any) => {
            tooltipString += `${point.series.name}: ${point.y.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}<br/>`;
          });
          return tooltipString;
        },
      };
    }

    // Ensure x-axis is numeric for proper sorting
    // chartOptions.xAxis = {
    //   ...chartOptions.xAxis,
    //   // type: 'linear',
    // };

    return chartOptions;
  }

  addChartOptionsData(delayed = false) {
    let graphs = { ...this.convo.graphs };
    Object.keys(graphs).forEach((graphType: any) => {
      let chartOptions = graphs[graphType];
      let chartProcess = new ChartDataProcess(
        graphType,
        chartOptions,
        this.convo.columnsMetadata,
        this.convo.queryResults,
        this.convo.tableColumns
      );
      graphs[graphType] = chartProcess.processChartOptions();
    });

    this.convo.graphs = graphs;
    this.updateHighChart(graphs[this.convo.selectedGraph]);
  }

  selectGraphXAxisMultiple(event: any) {
    let values: any = [];
    if (this.convo.selectedGraph == "StackBar" && event.values.length > 2) {
      values = event.values.slice(-2);
    } else if (
      event.values.length >= 1 &&
      this.convo.selectedGraph != "StackBar"
    ) {
      values = [event.values[event.values.length - 1]];
    } else {
      values = event.values;
    }
    this.convo.graphs[this.convo.selectedGraph].xAxisCols = values;
    this.addChartOptionsData();
  }

  selectGraphYAxisMultiple(event: any) {
    let values: any = [];
    if (
      event.values.length >= 1 &&
      ["Pie", "Hist", "StackBar"].includes(this.convo.selectedGraph)
    ) {
      values = [event.values[event.values.length - 1]];
    } else {
      values = event.values;
    }
    this.convo.graphs[this.convo.selectedGraph].yAxisCols = values;
    this.addChartOptionsData();
  }

  selectGraphGroupByMultiple(event: any) {
    let values: any = [];
    if (
      event.values.length >= 1 &&
      ["Line"].includes(this.convo.selectedGraph)
    ) {
      values = [event.values[event.values.length - 1]];
    } else {
      values = event.values;
    }
    this.convo.graphs[this.convo.selectedGraph].groupByCols = values;
    this.addChartOptionsData();
  }

  addBarLinePlotAreaChartData(chartOptions: any, graphType: string) {
    // Graph type specific settings
    if (graphType === "Bar" || graphType === "Hist") {
      chartOptions.chart.type = "column";
    } else if (graphType === "Line" || graphType === "Plot") {
      if (chartOptions?.group_by) {
        chartOptions.groupByCols = [chartOptions.group_by];
      } else {
        chartOptions.groupByCols = [];
      }
      chartOptions.chart.type = "line";
    } else if (graphType == "Stem") {
      chartOptions.chart.type = "lollipop";
    } else if (graphType === "Scatter" || graphType === "Eventplot") {
      chartOptions.chart.type = "scatter";
    } else if (graphType === "Stairs") {
      chartOptions.rangeSelected = {
        selected: 1,
      };
      chartOptions.plotOptions = {
        line: {
          step: "left",
        },
      };
    } else if (graphType === "Ecdf") {
      chartOptions.chart.type = "line";
    } else if (graphType === "Fill_between") {
      chartOptions.chart.type = "arearange";
    } else if (graphType === "Stackplot") {
      chartOptions.chart.type = "area";
      chartOptions.plotOptions = {
        area: {
          stacking: "normal",
        },
      };
    }

    // Assign X Axis data
    if (!chartOptions?.xAxisCols) {
      if (!chartOptions?.xAxis?.title?.text) {
        chartOptions.xAxisCols = [this.convo.tableColumns.at(0)];
      } else {
        chartOptions.xAxisCols = chartOptions.xAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }
    chartOptions.xAxis = {
      title: {
        text: chartOptions.xAxisCols[0],
      },
    };

    if (graphType == "Stem") {
      chartOptions.xAxis = {
        ...chartOptions.xAxis,
        type: "category",
      };
    }

    // Assign Y Axis data
    if (!chartOptions?.yAxisCols) {
      if (!chartOptions?.yAxis?.title?.text) {
        chartOptions.yAxisCols = [this.convo.tableColumns.at(1)];
      } else {
        chartOptions.yAxisCols = chartOptions.yAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }
    chartOptions.yAxis = {
      title: {
        text: chartOptions.yAxisCols.join(" & "),
      },
    };

    delete chartOptions.series;
    let data = [
      [
        ...chartOptions.xAxisCols.map((col: any) => this.getValueWithType(col)),
        ...chartOptions.yAxisCols.map((col: any) => {
          return this.getValueWithType(col);
        }),
      ],
    ];
    this.convo.queryResults.map((values: any) => {
      data.push(
        data[0].map((cols: any) => {
          return this.getValueWithType(values[cols]);
        })
      );
    });

    chartOptions.data = {
      rows: data,
    };
    chartOptions.tooltip = {
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormatter: function () {
        // Format point.y with commas as thousands separator
        const formattedY =
          typeof this.y === "number"
            ? this.y.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : this.y;
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedY}</b><br/>`;
      },
    };
    return chartOptions;
  }

  addPieChartData(chartOptions: any) {
    if (!chartOptions?.xAxisCols) {
      if (!chartOptions?.xAxis?.title?.text) {
        chartOptions.xAxisCols = [this.convo.tableColumns.at(0)];
      } else {
        let xAxis = chartOptions.xAxis.title.text;
        if (xAxis.includes("&")) {
          xAxis = xAxis.split("&")[0];
        }
        chartOptions.xAxisCols = [xAxis];
      }
    }
    if (!chartOptions?.yAxisCols) {
      if (!chartOptions?.yAxis?.title?.text) {
        chartOptions.yAxisCols = [this.convo.tableColumns.at(1)];
      } else {
        let yAxis = chartOptions.yAxis.title.text;
        if (yAxis.includes("&")) {
          yAxis = yAxis.split("&")[0];
        }
        chartOptions.yAxisCols = [yAxis];
      }
    }
    delete chartOptions.series;
    let data = [
      [
        ...chartOptions.xAxisCols.map((col: any) => this.getValueWithType(col)),
        ...chartOptions.yAxisCols.map((col: any) => {
          return this.getValueWithType(col);
        }),
      ],
    ];
    chartOptions.plotOptions = {};
    this.convo.queryResults.map((values: any) => {
      data.push(
        data[0].map((cols: any, index: number) => {
          if (index === 0) {
            // Add a zero-width space to ensure this stays a string and is not converted to a number
            return this.getValueWithType(values[cols]).toString() + "\u200B";
          }
          return this.getValueWithType(values[cols]);
        })
      );
    });

    if (!chartOptions?.rowOrCol) {
      chartOptions.rowOrCol = "row";
    }

    if (chartOptions.rowOrCol === "row") {
      chartOptions.data = {
        rows: data,
      };
    } else {
      chartOptions.data = {
        columns: data,
      };
    }

    chartOptions.tooltip = {
      pointFormatter: function () {
        const formattedY =
          typeof this.y === "number"
            ? this.y.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : this.y;
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedY}</b><br/>`;
      },
    };

    return chartOptions;
  }

  addStackedBarChart(chartOptions: any) {
    chartOptions.chart.type = "column";
    if (!chartOptions?.xAxisCols) {
      if (!chartOptions?.xAxis?.title?.text) {
        chartOptions.xAxisCols = [
          this.convo.tableColumns.at(0),
          this.convo.tableColumns.at(1),
        ];
      } else {
        chartOptions.xAxisCols = chartOptions.xAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }

    chartOptions.xAxis = {
      title: {
        text: chartOptions.xAxisCols[0],
      },
    };

    if (!chartOptions?.yAxisCols) {
      if (!chartOptions?.yAxis?.title?.text) {
        chartOptions.yAxisCols = [this.convo.tableColumns.at(1)];
      } else {
        chartOptions.yAxisCols = chartOptions.yAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }
    chartOptions.yAxis = {
      title: {
        text: chartOptions.yAxisCols.join(" & "),
      },
    };
    delete chartOptions.series;

    let data = [
      [
        ...chartOptions.xAxisCols.map((col: any) => this.getValueWithType(col)),
        ...chartOptions.yAxisCols.map((col: any) => {
          return this.getValueWithType(col);
        }),
      ],
    ];
    this.convo.queryResults.map((values: any) => {
      data.push(
        data[0].map((cols: any) => {
          return this.getValueWithType(values[cols]);
        })
      );
    });

    const groups = new Set([...data.slice(1).map((item) => item[0])]);
    const categories = new Set([...data.slice(1).map((item) => item[1])]);
    const outputData: { name: string; data: (number | null)[] }[] = [];

    // Populate the output data
    for (let category of categories) {
      const entry: any = { name: category, data: [] };
      for (let group of groups) {
        let value: number | null = null;
        for (let i = 1; i < data.length; i++) {
          const [dataGroup, dataCategory, dataValue] = data[i];
          if (dataGroup === group && dataCategory === category) {
            value = dataValue;
            break;
          }
        }
        entry.data.push(value);
      }
      outputData.push(entry);
    }

    // Fill in the missing categories
    for (let category of categories) {
      const entry = outputData.find((item) => item.name === category);
      if (entry && entry.data.length < groups.size) {
        while (entry.data.length < groups.size) {
          entry.data.push(null);
        }
      }
    }
    chartOptions.xAxis.categories = [...groups];
    chartOptions.tooltip = {
      formatter: function () {
        const formatNumber = (val: any) =>
          typeof val === "number"
            ? val.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : val;
        return (
          "<b>" +
          formatNumber(this.key) +
          "</b><br/>" +
          this.series.name +
          ": <b>" +
          formatNumber(this.y) +
          "</b><br/>" +
          "Total: <b>" +
          formatNumber(this.point.stackTotal) +
          "</b>"
        );
      },
    };
    chartOptions.series = outputData;
    chartOptions.plotOptions = {
      column: {
        stacking: "normal",
      },
    };
    return chartOptions;
  }

  addEcdfChartData(chartOptions: any) {
    chartOptions.chart.type = "line";
    chartOptions.xAxisCols = [];
    chartOptions.xAxis = {};

    // Assigning Y Axis data
    if (!chartOptions?.yAxisCols) {
      chartOptions.yAxisCols = [this.convo.tableColumns.at(0)];
      let yAxisCols = chartOptions.series.map(
        (seriesData: any) => seriesData.name
      );
      if (!yAxisCols) {
        chartOptions.yAxisCols = yAxisCols;
      }
    }
    chartOptions.yAxis = {
      title: {
        text: chartOptions.yAxisCols.join(" & "),
      },
    };
    chartOptions.marker = {
      enabled: false,
    };
    chartOptions.tooltip = {
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormatter: function () {
        // Format point.y with commas as thousands separator
        const formattedY =
          typeof this.y === "number"
            ? this.y.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : this.y;
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedY}</b><br/>`;
      },
    };
    chartOptions.series = chartOptions.yAxisCols.map((col: any) => {
      return {
        name: col,
        data: this.convo.queryResults.map((values: any) => {
          return this.getValueWithType(values[col]);
        }),
      };
    });

    return chartOptions;
  }

  addLineChartData(chartOptions: any) {
    // Ensure chart type is line
    chartOptions.chart = chartOptions.chart || {};
    chartOptions.chart.type = "line";

    // Default X and Y axis columns if not provided
    if (!chartOptions?.xAxisCols) {
      if (!chartOptions?.xAxis?.title?.text) {
        chartOptions.xAxisCols = [this.convo.tableColumns.at(0)];
      } else {
        chartOptions.xAxisCols = chartOptions.xAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }
    chartOptions.xAxis = {
      title: {
        text: chartOptions.xAxisCols[0],
      },
    };
    if (!chartOptions?.yAxisCols) {
      if (!chartOptions?.yAxis?.title?.text) {
        chartOptions.yAxisCols = [this.convo.tableColumns.at(1)];
      } else {
        chartOptions.yAxisCols = chartOptions.yAxis.title.text
          .split("&")
          .map((item: any) => item.trim());
      }
    }
    chartOptions.yAxis = {
      title: {
        text: chartOptions.yAxisCols.join(" & "),
      },
    };

    delete chartOptions.series;
    delete chartOptions.data;
    if (
      chartOptions.group_by === "" ||
      chartOptions.group_by === undefined ||
      chartOptions.group_by === null
    ) {
      this.disableGroupBy = true;
    }

    // Group by column (optional)
    if (chartOptions.groupByCols === undefined) {
      if (chartOptions.group_by) {
        chartOptions.groupByCols = [chartOptions.group_by];
      } else {
        chartOptions.groupByCols = [];
      }
    }

    if (chartOptions.groupByCols.length > 0) {
      chartOptions = this.processLineChartGroupBy(chartOptions);
    } else {
      let data = [
        [
          ...chartOptions.xAxisCols.map((col: any) =>
            this.getValueWithType(col)
          ),
          ...chartOptions.yAxisCols.map((col: any) => {
            return this.getValueWithType(col);
          }),
        ],
      ];

      // sort data by x-axis values
      this.convo.queryResults.sort((a: any, b: any) => {
        const aNumValue = this.isMonthString(a[chartOptions.xAxisCols[0]])
          ? new Date(a[chartOptions.xAxisCols[0]])
          : a[chartOptions.xAxisCols[0]];
        const bNumValue = new Date(b[chartOptions.xAxisCols[0]])
          ? new Date(b[chartOptions.xAxisCols[0]])
          : b[chartOptions.xAxisCols[0]];
        const aValue = this.getValueWithType(aNumValue);
        const bValue = this.getValueWithType(bNumValue);
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      });
      this.convo.queryResults.map((values: any) => {
        data.push(
          data[0].map((cols: any) => {
            return this.getValueWithType(values[cols]);
          })
        );
      });

      chartOptions.data = {
        rows: data,
      };
      chartOptions.tooltip = {
        headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
        pointFormatter: function () {
          // Format point.y with commas as thousands separator
          const formattedY =
            typeof this.y === "number"
              ? this.y.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : this.y;
          return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedY}</b><br/>`;
        },
      };
    }

    return chartOptions;
  }
}
